import React, { useEffect, useState } from 'react';
import { staffJSON } from './staffApi';

export default function AdminAccessPolicy() {
  const [saved, setSaved] = useState(null);
  const [mode, setMode] = useState('standard');
  const [ranges, setRanges] = useState('');
  const [password, setPassword] = useState('');
  const [testToken, setTestToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const load = async () => {
    const data = await staffJSON('/access-policy');
    setSaved(data); setMode(data.mode); setRanges(data.ranges.join('\n')); setTestToken(null);
  };
  useEffect(() => { let active = true;
    staffJSON('/access-policy').then(data => {
      if (active) { setSaved(data); setMode(data.mode); setRanges(data.ranges.join('\n')); }
    }).catch(err => { if (active) setError(err.message); });
    return () => { active = false; };
  }, []);
  const edit = setter => event => { setter(event.target.value); setTestToken(null); setMessage(''); setError(''); };
  const candidate = () => ({ mode, ranges: ranges.split(/[\n,]/).map(value => value.trim()).filter(Boolean) });
  const test = async () => {
    setBusy(true); setTestToken(null); setError(''); setMessage('');
    try {
      const result = await staffJSON('/access-policy/test', { method: 'POST', body: JSON.stringify(candidate()) });
      setTestToken(result.testToken);
      if (result.allowed) setMessage(result.message); else setError(result.message);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const save = async event => {
    event.preventDefault();
    if (!testToken || busy) return;
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await staffJSON('/access-policy', { method: 'PUT', body: JSON.stringify({ ...candidate(), testToken, currentPassword: password }) });
      await load(); setMessage(result.message);
    } catch (err) { setTestToken(null); setError(err.message); }
    finally { setPassword(''); setBusy(false); }
  };
  return <div className="staff-workspace"><section className="staff-panel" aria-label="Shared staff access policy">
    <h2>Admin access policy</h2>
    <p>This is one shared policy for all internal staff, including Marketing. Staff sign-in and MFA remain required in both modes.</p>
    {error && <p role="alert" className="staff-inline-error">{error}</p>}
    {!saved ? <p role="status">{error ? 'Access policy could not be loaded. Reload this page to try again.' : 'Loading access policy...'}</p> : <>
      <p>Current policy: <strong>{saved.mode === 'vpn' ? 'Company VPN required' : 'Standard'}</strong>. Your verified source IP: <strong>{saved.sourceIp || 'Unavailable'}</strong>.</p>
      {!saved.canManage && <p>Administrator access is required to configure this shared policy. Marketing accounts cannot change it.</p>}
      <form className="staff-mfa-form" onSubmit={save}>
        <fieldset disabled={busy || !saved.canManage}>
          <label>Access mode<select aria-label="Access mode" value={mode} onChange={edit(setMode)}>
            <option value="standard">Standard: staff sign-in and MFA</option>
            <option value="vpn">Restricted: approved company VPN IP ranges</option>
          </select></label>
          <label>Approved public IP addresses or ranges<textarea aria-label="Approved public IP addresses or ranges" rows={5} value={ranges} onChange={edit(setRanges)} placeholder="One public IP address or CIDR range per line" /></label>
          <small>Use your company VPN's fixed public exit addresses. Test from the VPN before enabling restrictions. Other staff must connect through an approved range too.</small>
          <div className="staff-actions"><button type="button" onClick={test}>{busy ? 'Checking...' : 'Test my access'}</button></div>
          <label>Confirm with your current staff password<input type="password" autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} required /></label>
          <div className="staff-actions"><button type="submit" className="staff-primary" disabled={!testToken || !password}>Save shared policy</button></div>
        </fieldset>
      </form>
      {message && <p role="status" className="staff-inline-success">{message}</p>}
      <p>Each successful access test is valid for five minutes and applies only to your current connection and the settings tested. Contact your platform operator if the VPN address changes and staff cannot sign in.</p>
      {!!saved.audit?.length && <details><summary>Recent policy changes</summary><ul>{saved.audit.map((entry, index) => <li key={index}>{String(entry.changed_at)} — staff {entry.actor_id ?? 'server operator'}, source {entry.source_ip || 'server recovery'}</li>)}</ul></details>}
    </>}
  </section></div>;
}
